import { Link } from 'react-router-dom';
import { IconBox } from '../components/Icons.jsx';
import './Services.css';

function Services() {
  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <p className="hero-eyebrow">Services</p>
          <h1 className="hero-title">Web Services Built for Business</h1>
          <p className="hero-subtitle">Custom development, managed hosting, and website care — delivered by specialists who understand what your business needs.</p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="services-section">
        <div className="services-container">
          <h2 className="section-title">What We Offer</h2>
          <p className="section-subtitle">Custom web development, modern applications, and managed hosting services for growing businesses</p>
          
          <div className="services-grid">
            {/* Custom Web Applications */}
            <div className="service-card detailed">
              <div className="service-header">
                <IconBox name="development" />
                <h3>Custom Web Applications</h3>
              </div>
              <div className="service-content">
                <p className="service-description">Modern web applications built with React, Vue, Node.js, and other cutting-edge technologies tailored to your business needs.</p>
                <ul className="service-features">
                  <li>React, Vue, Angular frontends</li>
                  <li>Node.js, Python, PHP backends</li>
                  <li>Database design & optimization</li>
                  <li>API development & integration</li>
                  <li>User authentication & management</li>
                  <li>Real-time features & notifications</li>
                  <li>Mobile-responsive design</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="cta-button">Get Quote</Link>
                </div>
              </div>
            </div>

            {/* Website Development */}
            <div className="service-card detailed">
              <div className="service-header">
                <IconBox name="design" />
                <h3>Website Development</h3>
              </div>
              <div className="service-content">
                <p className="service-description">Custom websites and content management systems built with the right technology for your business, from WordPress to static sites.</p>
                <ul className="service-features">
                  <li>WordPress & custom CMS</li>
                  <li>Static sites (Gatsby, Next.js)</li>
                  <li>E-commerce solutions</li>
                  <li>Content management systems</li>
                  <li>SEO optimization</li>
                  <li>Performance optimization</li>
                  <li>Responsive design</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="cta-button">Get Quote</Link>
                </div>
              </div>
            </div>

            {/* Managed Hosting */}
            <div className="service-card detailed">
              <div className="service-header">
                <IconBox name="hosting" />
                <h3>Managed Hosting</h3>
              </div>
              <div className="service-content">
                <p className="service-description">Reliable hosting and maintenance for any web application with security, backups, and performance optimization.</p>
                <ul className="service-features">
                  <li>Cloud hosting (AWS, Google Cloud)</li>
                  <li>Security monitoring & updates</li>
                  <li>Automated backups</li>
                  <li>Performance optimization</li>
                  <li>SSL certificates</li>
                  <li>24/7 monitoring</li>
                  <li>Email support</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="cta-button">Get Quote</Link>
                </div>
              </div>
            </div>

            {/* API & Integrations */}
            <div className="service-card detailed">
              <div className="service-header">
                <IconBox name="api" />
                <h3>API & Integrations</h3>
              </div>
              <div className="service-content">
                <p className="service-description">Custom APIs, third-party integrations, and database design to connect your systems and streamline operations.</p>
                <ul className="service-features">
                  <li>Custom API development</li>
                  <li>Third-party integrations</li>
                  <li>Database design & optimization</li>
                  <li>Payment gateway integration</li>
                  <li>CRM & marketing tool connections</li>
                  <li>Data migration services</li>
                  <li>System architecture consulting</li>
                </ul>
                <div className="service-cta">
                  <Link to="/contact" className="cta-button">Get Quote</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="portfolio-section">
        <div className="portfolio-container">
          <h2 className="section-title">Recent Work</h2>
          <p className="section-subtitle">See what we've built for our clients</p>
          
          <div className="portfolio-grid">
            <div className="portfolio-item">
              <div className="portfolio-image">
                <div className="portfolio-placeholder">
                  <div className="portfolio-icon">🍷</div>
                  <p>SF Chronicle Wine Competition</p>
                </div>
              </div>
              <div className="portfolio-content">
                <h3>San Francisco Chronicle Wine Competition</h3>
                <p className="portfolio-description">
                  Official website for the prestigious San Francisco Chronicle Wine Competition. 
                  Features competition information, registration details, event history, and 
                  announcements.
                </p>
                <div className="portfolio-tech">
                  <span className="tech-tag">WordPress</span>
                  <span className="tech-tag">Webmastering</span>
                  <span className="tech-tag">Database</span>
                  <span className="tech-tag">PHP</span>
                </div>
                <div className="portfolio-links">
                  <a href="https://winejudging.com" target="_blank" rel="noopener noreferrer" className="portfolio-link">
                    View Live Site →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Comparison */}
      <section className="features-section">
        <div className="features-container">
          <h2 className="section-title">Why Choose Our Services?</h2>
          <p className="section-subtitle">As a small, focused team, we provide personalized attention and custom solutions to every client</p>
          
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">🎯</div>
              <div className="feature-content">
                <h4>Custom Solutions</h4>
                <p>We build applications tailored to your specific business needs, not one-size-fits-all solutions that don't quite fit.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">⚡</div>
              <div className="feature-content">
                <h4>Modern Technology</h4>
                <p>Built with cutting-edge technologies like React, Node.js, and cloud infrastructure for optimal performance and scalability.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">📈</div>
              <div className="feature-content">
                <h4>Scalable Architecture</h4>
                <p>We design systems that grow with your business, from startup to enterprise scale, without requiring complete rebuilds.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">🤝</div>
              <div className="feature-content">
                <h4>Partnership Approach</h4>
                <p>We work as your technology partner, not just a vendor, providing ongoing support, strategy, and guidance.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">🔒</div>
              <div className="feature-content">
                <h4>Enterprise Security</h4>
                <p>Built-in security best practices, SSL certificates, and regular security updates to protect your business and data.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <div className="feature-content">
                <h4>Performance Optimized</h4>
                <p>Continuous monitoring and optimization to ensure your applications perform at their best and provide great user experiences.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="services-section">
        <div className="services-container">
          <h2 className="section-title">Our Process</h2>
          <p className="section-subtitle">How we work with you to deliver quality results</p>
          
          <div className="process-grid">
            <div className="process-step">
              <div className="step-number">1</div>
              <h4>Consultation</h4>
              <p>We start with a detailed consultation to understand your business needs, goals, and technical requirements.</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">2</div>
              <h4>Planning</h4>
              <p>We create a comprehensive plan with timelines, milestones, and deliverables tailored to your project.</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">3</div>
              <h4>Development</h4>
              <p>We build your solution using best practices and modern technologies, keeping you updated throughout the process.</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">4</div>
              <h4>Launch</h4>
              <p>We handle the deployment and launch process, ensuring everything runs smoothly from day one.</p>
            </div>
            
            <div className="process-step">
              <div className="step-number">5</div>
              <h4>Support</h4>
              <p>Ongoing support and maintenance to keep your solution running optimally and growing with your business.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Get Started?</h2>
          <p>Let's discuss your project and find the perfect solution for your business needs.</p>
          <Link to="/contact" className="cta-button">Start Your Project</Link>
        </div>
      </section>
    </div>
  );
}

export default Services;
